import React from "react";
import { useNavigate } from "react-router-dom";

const SUPPORTS = [
  "IEP",
  "504",
  "RTI Tier 1",
  "RTI Tier 2",
  "RTI Tier 3",
  "Counseling only",
  "N/A",
];
const GRADE_UNKNOWN = "Unknown";

function FiltersBar({
  supportOpen,
  setSupportOpen,
  gradeOpen,
  setGradeOpen,
  selectedSupports,
  setSelectedSupports,
  selectedGrades,
  setSelectedGrades,
  gradeOptions,
}) {
  const supportRef = React.useRef(null);
  const gradeRef = React.useRef(null);

  React.useEffect(() => {
    function onClick(e) {
      if (
        supportOpen &&
        supportRef.current &&
        !supportRef.current.contains(e.target)
      ) {
        setSupportOpen(false);
      }
      if (
        gradeOpen &&
        gradeRef.current &&
        !gradeRef.current.contains(e.target)
      ) {
        setGradeOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [supportOpen, gradeOpen, setSupportOpen, setGradeOpen]);

  const toggleSupport = (opt) => {
    setSelectedSupports((prev) =>
      prev.includes(opt) ? prev.filter((r) => r !== opt) : [...prev, opt]
    );
  };
  const toggleGrade = (opt) => {
    setSelectedGrades((prev) =>
      prev.includes(opt) ? prev.filter((r) => r !== opt) : [...prev, opt]
    );
  };

  const clearAll = () => {
    setSelectedSupports([]);
    setSelectedGrades([]);
  };

  return (
    <div
      style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}
    >
      {/* Support Status filter */}
      <div style={{ position: "relative" }} ref={supportRef}>
        <button
          type="button"
          onClick={() => setSupportOpen((o) => !o)}
          style={{
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            padding: "10px 12px",
            cursor: "pointer",
            minWidth: 220,
          }}
        >
          Support:{" "}
          {selectedSupports.length === 0
            ? "All"
            : `${selectedSupports.length} selected`}
          <span style={{ marginLeft: 8, color: "#64748b" }}>▾</span>
        </button>
        {supportOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              padding: 8,
              zIndex: 20,
              width: 260,
              maxHeight: 260,
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {SUPPORTS.map((opt) => (
              <label
                key={opt}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSupports.includes(opt)}
                  onChange={() => toggleSupport(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Grade filter */}
      <div style={{ position: "relative" }} ref={gradeRef}>
        <button
          type="button"
          onClick={() => setGradeOpen((o) => !o)}
          style={{
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            padding: "10px 12px",
            cursor: "pointer",
            minWidth: 160,
          }}
        >
          Grade:{" "}
          {selectedGrades.length === 0
            ? "All"
            : `${selectedGrades.length} selected`}
          <span style={{ marginLeft: 8, color: "#64748b" }}>▾</span>
        </button>
        {gradeOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              padding: 8,
              zIndex: 20,
              width: 200,
              maxHeight: 260,
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {gradeOptions.map((opt) => (
              <label
                key={opt}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedGrades.includes(opt)}
                  onChange={() => toggleGrade(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {(selectedSupports.length > 0 || selectedGrades.length > 0) && (
        <button
          type="button"
          onClick={clearAll}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

const SupportPlans = () => {
  const navigate = useNavigate();
  const [students, setStudents] = React.useState([]);
  const [filtered, setFiltered] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [userProfile, setUserProfile] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [supportOpen, setSupportOpen] = React.useState(false);
  const [gradeOpen, setGradeOpen] = React.useState(false);
  const [selectedSupports, setSelectedSupports] = React.useState([]);
  const [selectedGrades, setSelectedGrades] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const PER_PAGE = 8;

  const normalizeSupports = (val) => {
    if (Array.isArray(val)) return val.length ? val : ["N/A"];
    if (typeof val === "string" && val.trim()) return [val.trim()];
    return ["N/A"];
  };
  const normalizeGrade = (val) => {
    if (val === 0) return "0";
    if (val === null || val === undefined) return GRADE_UNKNOWN;
    const g = String(val).trim();
    return g ? g : GRADE_UNKNOWN;
  };

  const gradeOptions = React.useMemo(() => {
    const set = new Set();
    let hasUnknown = false;
    for (const s of students) {
      const g = normalizeGrade(s.grade);
      if (g === GRADE_UNKNOWN) hasUnknown = true;
      else set.add(g);
    }
    const arr = Array.from(set);
    arr.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
    return hasUnknown ? [GRADE_UNKNOWN, ...arr] : arr;
  }, [students]);

  // Fetch students + profile
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const [resStudents, resProfile] = await Promise.all([
          fetch(`${apiUrl.replace(/\/$/, "")}/students`, {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }),
          fetch(`${apiUrl.replace(/\/$/, "")}/me`, {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }),
        ]);
        if (!resStudents.ok) throw new Error("Failed to fetch students");
        if (!resProfile.ok) throw new Error("Failed to fetch user profile");
        const dataStudents = await resStudents.json();
        const profile = await resProfile.json();
        setStudents(dataStudents.students || []);
        setFiltered(dataStudents.students || []);
        setUserProfile(profile);
      } catch (e) {
        setError(e.message || "Error loading data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Apply search + filters
  React.useEffect(() => {
    const q = search.toLowerCase().trim();
    const result = students.filter((s) => {
      // Search
      if (q) {
        const first = s.firstName?.toLowerCase() || "";
        const last = s.lastName?.toLowerCase() || "";
        const name = s.name?.toLowerCase() || "";
        const id = s.studentId ? String(s.studentId).toLowerCase() : "";
        const gradeStr = s.grade ? String(s.grade).toLowerCase() : "";
        const matchesSearch =
          first.includes(q) ||
          last.includes(q) ||
          name.includes(q) ||
          id.includes(q) ||
          gradeStr.includes(q);
        if (!matchesSearch) return false;
      }
      // Supports
      const supports = normalizeSupports(s.studentSupportStatus);
      if (
        selectedSupports.length > 0 &&
        !supports.some((st) => selectedSupports.includes(st))
      )
        return false;
      // Grade
      const g = normalizeGrade(s.grade);
      if (selectedGrades.length > 0 && !selectedGrades.includes(g))
        return false;
      return true;
    });
    setFiltered(result);
    setPage(1);
  }, [search, students, selectedSupports, selectedGrades]);

  // Clamp page when filtered changes
  const totalPages = Math.max(1, Math.ceil((filtered?.length || 0) / PER_PAGE));
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [filtered, page, totalPages]);

  const startIdx = (page - 1) * PER_PAGE;
  const paginated = filtered.slice(startIdx, startIdx + PER_PAGE);

  const displayName = userProfile?.firstName || "Guest";

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh" }}>
      <nav
        style={{
          background: "linear-gradient(90deg, #23395d 60%, #2d4a7a 100%)",
          color: "#fff",
          padding: "0 0 0 32px",
          display: "flex",
          alignItems: "center",
          height: 64,
          fontWeight: 500,
          fontSize: 18,
          letterSpacing: 0.5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 22, marginRight: 40 }}>
          COUNSELOR NOTES
        </span>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Home
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/support-plans")}
        >
          Support Plans
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/analysis")}
        >
          Analysis
        </button>
        {(userProfile?.role === "admin" ||
          userProfile?.role === "districtAdmin" ||
          userProfile?.role === "auditor") && (
          <button
            style={{
              color: "#fff",
              background: "none",
              border: "1px solid rgba(255,255,255,0.6)",
              textDecoration: "none",
              marginRight: 12,
              font: "inherit",
              cursor: "pointer",
              borderRadius: 6,
              padding: "6px 10px",
            }}
            onClick={() => navigate("/admin-tools")}
          >
            Admin Tools
          </button>
        )}
        <button
          onClick={() => {
            localStorage.removeItem("token");
            navigate("/login");
          }}
          style={{
            background: "#fff",
            color: "#23395d",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 16,
            padding: "8px 22px",
            cursor: "pointer",
            marginLeft: "auto",
            marginRight: 24,
          }}
        >
          Log Out
        </button>
      </nav>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 600, margin: "32px 0 8px 0" }}>
          Support Plans
        </h1>
        <div style={{ color: "#64748b", marginBottom: 16 }}>
          Welcome, {displayName}. View which students are on which support
          plans. Notes are not shown here.
        </div>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <input
            type="text"
            placeholder="Search by name, student ID, or grade"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: 14,
              fontSize: 16,
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              marginRight: 12,
              background: "#fff",
            }}
          />
          <button
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 18,
              padding: "12px 32px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </div>

        {/* Filters */}
        <FiltersBar
          supportOpen={supportOpen}
          setSupportOpen={setSupportOpen}
          gradeOpen={gradeOpen}
          setGradeOpen={setGradeOpen}
          selectedSupports={selectedSupports}
          setSelectedSupports={setSelectedSupports}
          selectedGrades={selectedGrades}
          setSelectedGrades={setSelectedGrades}
          gradeOptions={gradeOptions}
        />

        {/* Table */}
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            marginTop: 18,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div style={{ padding: 32, textAlign: "center" }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: "center", color: "#ef4444" }}>
              {error}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f1f5f9" }}>
                <tr>
                  <th style={{ textAlign: "left", padding: 16 }}>Student</th>
                  <th style={{ textAlign: "left", padding: 16 }}>Grade</th>
                  <th style={{ textAlign: "left", padding: 16 }}>
                    Support Plan(s)
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{ padding: 32, textAlign: "center" }}
                    >
                      No students match your filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((s, i) => (
                    <tr
                      key={s.studentId || i}
                      style={{
                        borderTop: i === 0 ? "none" : "1px solid #e5e7eb",
                      }}
                    >
                      <td style={{ padding: 16 }}>
                        {(s.firstName || s.name || "") +
                          (s.lastName ? ` ${s.lastName}` : "")}
                      </td>
                      <td style={{ padding: 16 }}>{normalizeGrade(s.grade)}</td>
                      <td style={{ padding: 16 }}>
                        {normalizeSupports(s.studentSupportStatus).join(", ")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {filtered.length > PER_PAGE && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              marginTop: 24,
            }}
          >
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: "8px 18px",
                borderRadius: 6,
                border: "none",
                background: page === 1 ? "#e5e7eb" : "#2563eb",
                color: page === 1 ? "#888" : "#fff",
                cursor: page === 1 ? "not-allowed" : "pointer",
              }}
            >
              ← Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: "8px 18px",
                borderRadius: 6,
                border: "none",
                background: page === totalPages ? "#e5e7eb" : "#2563eb",
                color: page === totalPages ? "#888" : "#fff",
                cursor: page === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPlans;
